import { forwardRef } from 'react';
import PhoneInput, {
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input';
import type {
  Value as E164Number,
  Country,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

type RPNIProps = React.ComponentProps<typeof PhoneInput>;

export interface PhoneFieldProps
  extends Omit<RPNIProps, 'value' | 'onChange' | 'defaultCountry' | 'limitMaxLength' | 'countryCallingCodeEditable' | 'initialValueFormat'> {
  value?: string;
  onChange: (value: string) => void;
  defaultCountry?: Country;
  wrapperClassName?: string;
}

export const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>(
  ({ value, onChange, defaultCountry = 'US', wrapperClassName, numberInputProps, ...rest }, _ref) => {
    const mergedNumberProps = {
      className: 'phone-number-input',
      ...(numberInputProps ?? {}),
    };

    return (
      <div className={`phone-input-wrapper${wrapperClassName ? ` ${wrapperClassName}` : ''}`}>
        <PhoneInput
          defaultCountry={defaultCountry}
          limitMaxLength
          countryCallingCodeEditable={false}
          initialValueFormat="national"
          value={(value || undefined) as E164Number | undefined}
          onChange={(v) => onChange((v as string | undefined) || '')}
          numberInputProps={mergedNumberProps}
          {...rest}
        />
      </div>
    );
  },
);
PhoneField.displayName = 'PhoneField';

/**
 * Normalize any stored phone string to "+{countryCode} {national}" (e.g. "+1 (813) 310-9451").
 *
 * Handles:
 *  - E.164 values stored by PhoneField (+15555551234)
 *  - Legacy free-form values ("813-310-9451", "(813) 310-9451", "8133109451") — assumed US
 *  - Anything unparseable falls back to the raw string so data is never dropped
 */
export function formatPhoneDisplay(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('+')) {
    try {
      const pn = parsePhoneNumber(trimmed as E164Number);
      if (pn) return `+${pn.countryCallingCode} ${pn.formatNational()}`;
    } catch {
      // fall through to legacy parse
    }
  }

  try {
    const pn = parsePhoneNumber(trimmed, 'US');
    if (pn && pn.isValid()) {
      return `+${pn.countryCallingCode} ${pn.formatNational()}`;
    }
  } catch {
    // fall through
  }

  return trimmed;
}

export { isValidPhoneNumber };
